const money = (
  cents,
  currency = "CAD",
  locale = document.documentElement.lang || "fr-CA",
) => {
  const amount = Number(cents) / 100;
  if (!Number.isFinite(amount)) return "";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const cacheKey = (productId, limit, locale) =>
  `minicartComplementary:${locale || "x"}:${productId}:${limit}`;

const getCartCurrency = () => {
  const el = document.querySelector("[data-ajax-cart-initial-state]");
  if (!el) return "CAD";
  try {
    const json = JSON.parse(el.textContent || "{}");
    return json.currency || "CAD";
  } catch {
    return "CAD";
  }
};

const escapeHtml = (str) =>
  String(str ?? "").replace(
    /[&<>"']/g,
    (m) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[m],
  );

const addItemToCart = async ({ variantId, buttonEl }) => {
  if (!variantId || !buttonEl) return;

  const originalHtml = buttonEl.innerHTML;
  const addingLabel = buttonEl.dataset.labelAdding || "";
  buttonEl.disabled = true;
  buttonEl.innerHTML = `<span class="minicart-complementary__btn-loader" aria-hidden="true"></span><span class="sr-only">${escapeHtml(addingLabel)}</span>`;

  try {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    if (window.Alpine?.store("global")) {
      window.Alpine.store("global").openMinicart();
    }
    if (window.Alpine?.store("minicart") && data?.items?.[0]) {
      window.Alpine.store("minicart").addProduct(data.items[0]);
    }

    if (typeof window.liquidAjaxCart?.update === "function") {
      window.liquidAjaxCart.update();
    }
  } catch {
    buttonEl.innerHTML = originalHtml;
  } finally {
    buttonEl.disabled = false;
    buttonEl.innerHTML = originalHtml;
  }
};

const wireAddToCart = (root) => {
  root.querySelectorAll("[data-minicart-complementary-add]").forEach((btn) => {
    if (btn.dataset.bound === "true") return;
    btn.dataset.bound = "true";
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      addItemToCart({
        variantId: btn.getAttribute("data-variant-id"),
        buttonEl: btn,
      });
    });
  });
};

const renderCard = (product, currency, labels = {}) => {
  const imageUrl =
    product?.featured_image?.url ||
    product?.featured_image ||
    product?.images?.[0] ||
    "";
  const imageAlt = product?.featured_image?.alt || product?.title || "";
  const price = product?.price ?? product?.variants?.[0]?.price;
  const variantId =
    product?.variants?.find?.((v) => v?.available)?.id ??
    product?.variants?.[0]?.id ??
    null;
  const url = product?.url || "#";
  const title = product?.title || "";

  return `
    <div class="minicart-complementary__card" role="listitem">
      <a class="minicart-complementary__image" href="${escapeHtml(url)}">
        ${
          imageUrl
            ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(imageAlt)}" loading="lazy">`
            : ""
        }
      </a>
      <div class="minicart-complementary__content">
        <a class="minicart-complementary__title" href="${escapeHtml(url)}">${escapeHtml(title)}</a>
        <div class="minicart-complementary__bottom">
          <span class="minicart-complementary__price">${money(price, currency)}</span>
          ${
            variantId
              ? `<button
                   type="button"
                   class="minicart-complementary__add"
                   aria-label="${escapeHtml(labels.add)}"
                   data-minicart-complementary-add
                   data-variant-id="${escapeHtml(variantId)}"
                   data-label-adding="${escapeHtml(labels.adding)}"
                 >+</button>`
              : `<a class="minicart-complementary__add" href="${escapeHtml(url)}" aria-label="${escapeHtml(labels.view)}">+</a>`
          }
        </div>
      </div>
    </div>
  `;
};

const fetchRecommendations = async ({ productId, limit, intent }) => {
  if (!window?.routes?.product_recommendations_url) return [];
  const url = `${window.routes.product_recommendations_url}.json?product_id=${encodeURIComponent(
    productId,
  )}&limit=${encodeURIComponent(limit)}&intent=${encodeURIComponent(intent)}`;

  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) return [];
  const data = await res.json();
  return data?.recommendations?.products || data?.products || [];
};

const wireSliderControls = (root) => {
  const slider = root.querySelector(".minicart-complementary__slider");
  const prev = root.querySelector("[data-minicart-complementary-prev]");
  const next = root.querySelector("[data-minicart-complementary-next]");
  if (!slider || !prev || !next) return;

  const step = () => {
    const first = slider.querySelector(".minicart-complementary__card");
    if (!first) return slider.clientWidth;
    return first.getBoundingClientRect().width + 12;
  };

  prev.addEventListener("click", () => {
    slider.scrollBy({ left: -step(), behavior: "smooth" });
  });

  next.addEventListener("click", () => {
    slider.scrollBy({ left: step(), behavior: "smooth" });
  });
};

const renderBlock = ({ products, currency, limit, labels }) => `
  <div class="minicart-complementary__header">
    <p class="minicart-complementary__heading">${escapeHtml(labels.heading)}</p>
    <div class="flex shrink-0 items-center justify-end gap-2">
      <button
        type="button"
        aria-label="${escapeHtml(labels.previous)}"
        data-minicart-complementary-prev
        class="w-12 h-12 items-center justify-center rounded-full bg-primary-3 flex touch-manipulation"
      >
        <span class="sr-only">${escapeHtml(labels.previous)}</span>
        <svg
          class="w-5 h-5 text-white transform rotate-180"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
      <button
        type="button"
        aria-label="${escapeHtml(labels.next)}"
        data-minicart-complementary-next
        class="w-12 h-12 items-center justify-center rounded-full bg-primary-3 flex touch-manipulation"
      >
        <span class="sr-only">${escapeHtml(labels.next)}</span>
        <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
    </div>
  </div>
  <div class="minicart-complementary__slider" role="list">
    ${products
      .slice(0, Number(limit))
      .map((p) => renderCard(p, currency, labels))
      .join("")}
  </div>
`;

const loadComplementary = async (root) => {
  const productId = root?.dataset?.productId;
  const limit = root?.dataset?.limit || "3";
  if (!productId || !window?.routes?.product_recommendations_url) return;

  const currency = getCartCurrency();
  const primaryIntent = root?.dataset?.intent || "complementary";
  const fallbackIntent = root?.dataset?.fallbackIntent || "related";
  const locale = root?.dataset?.locale || "";
  const labels = {
    heading: root?.dataset?.heading || "",
    previous: root?.dataset?.labelPrevious || "",
    next: root?.dataset?.labelNext || "",
    add: root?.dataset?.labelAdd || "",
    view: root?.dataset?.labelView || "",
    adding: root?.dataset?.labelAdding || "",
  };

  const key = cacheKey(productId, limit, locale);
  const cached = sessionStorage.getItem(key);
  if (cached && !root.innerHTML) {
    root.innerHTML = cached;
    root.setAttribute("data-state", "ready");
  }

  if (
    root.getAttribute("data-state") === "ready" &&
    root.getAttribute("data-loaded-product-id") === productId
  ) {
    return;
  }

  root.setAttribute("data-state", "loading");
  root.setAttribute("data-loaded-product-id", productId);

  try {
    let products = await fetchRecommendations({
      productId,
      limit,
      intent: primaryIntent,
    });
    if (!Array.isArray(products) || products.length === 0) {
      products = await fetchRecommendations({
        productId,
        limit,
        intent: fallbackIntent,
      });
    }
    if (!Array.isArray(products) || products.length === 0) {
      root.innerHTML = "";
      root.setAttribute("data-state", "empty");
      return;
    }

    const html = renderBlock({ products, currency, limit, labels });
    root.innerHTML = html;
    sessionStorage.setItem(key, html);
    root.setAttribute("data-state", "ready");
    wireSliderControls(root);
    wireAddToCart(root);
  } catch {
    root.setAttribute("data-state", "error");
  }
};

const boot = () => {
  const root = document.querySelector("[data-minicart-complementary]");
  if (!root) return;
  loadComplementary(root);
};

document.addEventListener("DOMContentLoaded", boot);
document.addEventListener("liquid-ajax-cart:queue-end", () => {
  const root = document.querySelector("[data-minicart-complementary]");
  if (!root) return;
  loadComplementary(root);
});
